/**
 * Atividade recente entre todas as empresas.
 *
 * Decisoes nao obvias:
 *  1. O nome do lead passa por `shortenName` mesmo aqui, numa lista curta:
 *     a Central fica aberta em tela de escritorio e quem ligou foi a IA;
 *     ninguem precisa do nome completo para acompanhar a plataforma. O
 *     evento nao traz telefone de proposito.
 *  2. Mostra `LIMITE` eventos e um "Ver mais": com centenas de empresas a
 *     lista de agora seria infinita, e o que importa e o ultimo minuto.
 */

import { useState } from 'react';
import { Button, CompanyLogo, Icon, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, shortenName } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import type { PlatformEvent } from '../types';
import { EVENT_KIND_CHIP, EVENT_KIND_ICON, EVENT_KIND_LABEL } from '../vocabulario';
import { identidadeDaEmpresa } from './IdentidadeEmpresa';

const LIMITE = 8;

function complemento(e: PlatformEvent): string | null {
  const partes: string[] = [];
  if (e.offerName !== null) {
    partes.push(e.offerName);
  }
  if (e.amountCents !== null) {
    partes.push(formatBRLFromCents(e.amountCents));
  }
  return partes.length === 0 ? null : partes.join(' · ');
}

export function AtividadeDaPlataforma({
  events,
  companies,
  className = '',
}: {
  readonly events: readonly PlatformEvent[];
  readonly companies: readonly CompanySummary[];
  readonly className?: string;
}): JSX.Element {
  const [expandido, setExpandido] = useState(false);
  const mostrados = expandido ? events : events.slice(0, LIMITE);
  const escondidos = events.length - mostrados.length;

  return (
    <SectionCard
      id="atividade-plataforma"
      title="Atividade recente da plataforma"
      icon="raio"
      description="Últimos eventos entre todas as empresas: carrinhos, ligações da IA, mensagens, vendas e estornos. Tudo depois do abandono ou do vencimento."
      className={className}
    >
      {events.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-subtle">Nenhum evento recente. Quando um checkout for abandonado ou um plano vencer, o motor age e o registro aparece aqui.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="divide-y divide-line/60">
            {mostrados.map((e) => {
              const identidade = identidadeDaEmpresa(e.companyId, companies);
              const detalhe = complemento(e);
              return (
                <li key={e.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5 text-sm">
                  <div className="flex min-w-0 items-center gap-3">
                    <span aria-hidden="true" className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${EVENT_KIND_CHIP[e.kind]}`}>
                      <Icon name={EVENT_KIND_ICON[e.kind]} size="sm" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">
                        {EVENT_KIND_LABEL[e.kind]}
                        {e.leadName === null ? null : <span className="font-normal text-ink-subtle"> · {shortenName(e.leadName)}</span>}
                      </p>
                      {detalhe === null ? null : <p className="truncate text-xs text-ink-subtle">{detalhe}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5 text-xs text-ink-muted">
                      <CompanyLogo company={identidade} size="sm" />
                      <span className="sr-only sm:not-sr-only">{identidade.name}</span>
                    </span>
                    <span className="tabular-nums text-xs text-ink-subtle">{formatDateTime(e.occurredAt)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
          {escondidos > 0 || (expandido && events.length > LIMITE) ? (
            <div className="flex justify-center">
              <Button variant="ghost" size="sm" icon="chevron" onClick={() => setExpandido((v) => !v)} className={expandido ? '[&>svg]:rotate-180' : ''}>
                {expandido ? 'Ver menos' : `Ver os outros ${formatCount(escondidos)} ${escondidos === 1 ? 'evento' : 'eventos'}`}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </SectionCard>
  );
}
